/**
 * Patient utilities - CNP validation, value helpers, constants
 */

// Time slots generator (08:00-20:00, every 15 min)
export const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 8; hour < 20; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
    }
  }
  return slots;
};

export const TIME_SLOTS = generateTimeSlots();

// CNP validation for Romanian personal identification number
export const validateCNP = (cnp) => {
  if (!cnp || cnp.length !== 13) return { valid: false, error: 'CNP-ul trebuie să aibă 13 cifre' };
  if (!/^\d{13}$/.test(cnp)) return { valid: false, error: 'CNP-ul trebuie să conțină doar cifre' };
  
  const firstDigit = parseInt(cnp[0]);
  if (![1, 2, 3, 4, 5, 6, 7, 8].includes(firstDigit)) {
    return { valid: false, error: 'Prima cifră a CNP-ului este invalidă' };
  }
  
  const controlKey = '279146358279';
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cnp[i]) * parseInt(controlKey[i]);
  }
  let checkDigit = sum % 11;
  if (checkDigit === 10) checkDigit = 1;
  
  if (parseInt(cnp[12]) !== checkDigit) {
    return { valid: false, error: 'Cifra de control a CNP-ului este invalidă' };
  }
  
  return { valid: true, error: null };
};

// Parse CNP to extract birth date and gender
export const parseCNP = (cnp) => {
  if (!cnp || cnp.length < 7) return null;
  
  const firstDigit = parseInt(cnp[0]);
  const year = cnp.substring(1, 3);
  const month = cnp.substring(3, 5);
  const day = cnp.substring(5, 7);
  
  let century = 1900;
  let gender = 'M';
  
  switch (firstDigit) {
    case 1: century = 1900; gender = 'M'; break;
    case 2: century = 1900; gender = 'F'; break;
    case 3: century = 1800; gender = 'M'; break;
    case 4: century = 1800; gender = 'F'; break;
    case 5: century = 2000; gender = 'M'; break;
    case 6: century = 2000; gender = 'F'; break;
    case 7: century = 1900; gender = 'M'; break;
    case 8: century = 1900; gender = 'F'; break;
    default: return null;
  }
  
  const fullYear = century + parseInt(year);
  const dateOfBirth = `${fullYear}-${month}-${day}`;
  
  const date = new Date(dateOfBirth);
  if (isNaN(date.getTime())) return null;
  
  return { dateOfBirth, gender };
};

// Normal values for lab parameters
export const NORMAL_VALUES = {
  glycemia: { min: 70, max: 100, unit: 'mg/dL', label: 'Glicemie à jeun' },
  hba1c: { min: 4, max: 5.7, unit: '%', label: 'HbA1c' },
  bmi: { min: 18.5, max: 24.9, unit: 'kg/m²', label: 'IMC' },
  hdl: { min: 40, max: 999, unit: 'mg/dL', label: 'HDL' },
  ldl: { min: 0, max: 100, unit: 'mg/dL', label: 'LDL' },
  triglycerides: { min: 0, max: 150, unit: 'mg/dL', label: 'Trigliceride' },
};

// Get color class based on value vs normal range
export const getValueColor = (param, value) => {
  if (value === null || value === undefined) return 'text-slate-500';
  const range = NORMAL_VALUES[param];
  if (!range) return 'text-slate-900';
  
  if (param === 'hdl') {
    return value >= range.min ? 'text-emerald-600' : 'text-red-600';
  }
  if (param === 'ldl' || param === 'triglycerides') {
    return value <= range.max ? 'text-emerald-600' : 'text-red-600';
  }
  
  if (value >= range.min && value <= range.max) {
    return 'text-emerald-600';
  }
  return 'text-red-600';
};

// Calculate age from date of birth
export const calculateAge = (dateOfBirth) => {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Calculate BMI
export const calculateBMI = (height, weight) => {
  if (!height || !weight) return null;
  const heightM = height / 100;
  return (weight / (heightM * heightM)).toFixed(1);
};

// Initial consultation form state
export const getInitialConsultationForm = (patientDiagnoses = []) => {
  const now = new Date();
  const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return {
    consultation_date: localDate,
    consultation_time: now.toTimeString().slice(0, 5),
    diagnosis_ids: patientDiagnoses,
    symptoms: '',
    clinical_exam: '',
    lab_results: '',
    glycemia: '',
    hba1c: '',
    blood_pressure: '',
    weight: '',
    hdl: '',
    ldl: '',
    triglycerides: '',
    other_lab_results: '',
    medications: [],
    recommendations: ''
  };
};

// Initial medication state
export const getInitialMedication = () => ({
  medication_id: '',
  commercial_name: '',
  inn_name: '',
  form: '',
  dose: '',
  ds: '',
  treatment_days: '',
  morning: false,
  lunch: false,
  evening: false,
  once_weekly: false,
  notes: ''
});
